import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BetArgs,
  BetPositionLabelType,
  BettingCartType,
  ChangeableKeys,
  OddsType,
  ParlayBetDTO,
  ParlayBetRequest,
  ParlayBetResponse,
  ParlayInfoData,
  ResultCode,
  SingleBetDTO,
  SingleBetPayload,
  SingleBetRequest,
  SingleBetResponse,
} from '../interface/bets';
import { BetType, OddDTO, TeamLeagueType } from '../interface/odds';
import { OddsAPI } from '../apis/odds/index';
import { BetsAPI } from '../apis/bets';
import { fetchBalance } from './balanceSlice';
import { RootState } from './types';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { doGetMiniBetList } from './betSlice';
import { getMatchByMatchGroupId, getMatchesByMatchIds } from './oddSlice';
import { ServiceUtils } from '../utils';
import { calculateOddByOddsType } from '../../../styleguide/src/components/BettingBox/useOddsTypeCalculator';
import { Utils } from '../../../styleguide/src/utils';
import { getSingleBetPayloadSelector } from '../../../styleguide/src/components/BettingCart/SingleBet/hooks';
import { getBetLimitBySportTypeSelector } from './userSlice';

export type CartSteps = 'BETTING' | 'VALIDATE' | 'CONFIRM' | 'PROCESSING' | 'SUCCESS' | 'ERROR' | 'CHANGED';
export interface CartSliceState {
  isOpen: boolean;
  type: any;
  cart: {
    currency: string;
    totalBet: string;
    totalBetAmount: string;
    singleBet: SingleBetPayload[];
    parlayBet?: ParlayBetRequest;
    parlayBetResultDetail?: ParlayBetResponse['ResultDetail'];
    parlayBetAmt: number;
    parlayInfo: ParlayInfoData[];
    isSingleBetExceeded: Record<string, boolean>;
    resultCode?: ResultCode;
  };
  bets: SingleBetDTO[] | ParlayBetDTO;
  singleBetSelectedId?: string;
  currentStep: CartSteps;
  betError?: string;
  pollingTs?: number;
}

const initialState: CartSliceState = {
  isOpen: false,
  type: BettingCartType.Single,
  cart: {
    currency: 'USD',
    totalBet: '0',
    totalBetAmount: '0',
    singleBet: [],
    parlayInfo: [],
    parlayBetAmt: 0,
    isSingleBetExceeded: {},
  },
  bets: [],
  currentStep: 'BETTING',
};

function removeRedundantFromBetArgs(betArgs: BetArgs) {
  const newBetArgs = { ...betArgs } as BetArgs;
  for (const key in newBetArgs) {
    delete newBetArgs.BetPositionLabel;
    if (key === 'id' || /^_/g.test(key)) {
      // @ts-ignore
      delete newBetArgs[key];
    }
  }
  return newBetArgs;
}

export const addMatchToSingleBet = createAsyncThunk(
  'cart/addMatchToSingleBet',
  async (payload: { Request: SingleBetRequest; Match?: OddDTO }) => {
    const matchDetail = payload.Match || (await OddsAPI.getOddInfoByMatchId(payload.Request.BetArgs.MatchID));

    return {
      ...payload.Request,
      Match: matchDetail ? matchDetail : payload.Match,
    } as SingleBetPayload;
  }
);

export const addMatchToParlayBet = createAsyncThunk(
  'cart/addMatchToParlayBet',
  async (payload: { Request: SingleBetRequest; Match?: OddDTO }) => {
    const matchDetail = payload.Match || (await OddsAPI.getOddInfoByMatchId(payload.Request.BetArgs.MatchID));
    return {
      ...payload.Request,
      Match: matchDetail ? matchDetail : payload.Match,
    };
  }
);

export const addMultipleMatchesToParlayBet = createAsyncThunk(
  'cart/addMultipleMatchesToParlayBet',
  async (payload: { Request: ParlayBetRequest; Match?: OddDTO }) => {
    const matchDetail = payload.Match || (await OddsAPI.getOddInfoByMatchId(payload.Request.BetArgs[0].MatchID));
    return {
      ...payload.Request,
      Match: matchDetail ? matchDetail : payload.Match,
    };
  }
);

export const placeSingleBet = createAsyncThunk('cart/placeSingleBet', async (_, { getState, dispatch }) => {
  const { cart, user } = getState() as RootState;
  const bets = cart.cart.singleBet;
  const responses = [] as { id: string; bet: SingleBetResponse }[];

  try {
    for (const payload of bets) {
      const { Match, ...request } = payload;
      const betType = request.BetArgs.BetType;
      const oddTypeBetArgs = request.BetArgs.OddsType;
      const isSpecialBetType = Utils.isSpecialBetType(betType);
      const oddType = isSpecialBetType ? oddTypeBetArgs : OddsType.DE;

      const newRequest = cloneDeep(request);
      newRequest.BetArgs.OddsType = oddType;

      const id = newRequest.BetArgs.id!;
      delete newRequest._changed;
      newRequest.BetArgs = removeRedundantFromBetArgs(newRequest.BetArgs);
      const response = await BetsAPI.placeSingleBet(newRequest, user);
      responses.push({ id, bet: response! });
    }
  } catch (e) {
    console.error(e);
    if (axios.isAxiosError(e)) {
      throw new Error(e.response?.data.Message?.replace(/^.*?message=/g, ''));
    }
  }

  await dispatch(fetchBalance());

  // iterate through responses, in case there is any response with result code=== match paused or odds changed,
  // set step is CHANGED
  let navigateToChanged = false;
  for (const { bet } of responses) {
    if (
      bet.ResultCode === ResultCode.MATCH_PAUSED ||
      bet.ResultCode === ResultCode.MATCH_HIDE ||
      bet.ResultCode === ResultCode.ODDS_CHANGED ||
      bet.ResultCode === ResultCode.SCORE_CHANGED ||
      bet.ResultCode === ResultCode.INSUFFICIENT_FUNDS
    ) {
      navigateToChanged = true;
      break;
    }
  }

  return { response: responses, navigateToChanged };
});

export const placeParlayBet = createAsyncThunk(
  'cart/placeParlayBet',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { cart: originalCart, user } = getState() as RootState;
    const cart = cloneDeep(originalCart);

    let bets = {
      ...cart.cart.parlayBet!,
      CombineFlag: 1,
      BetterOdds: true,
      AOSCores: 0,
    };

    try {
      const response = await BetsAPI.placeParlayBet(bets, user);

      if (
        response!.ResultCode === ResultCode.BET_FAILED ||
        response!.ResultCode === ResultCode.MATCH_HIDE ||
        response!.ResultCode === ResultCode.MATCH_PAUSED
      ) {
        return rejectWithValue(!isEmpty(response!.ResultRemark) ? response!.ResultRemark : 'Unknown error -20');
      }

      if (!Object.keys(ResultCode).includes(response!.ResultCode.toString())) {
        return rejectWithValue(
          !isEmpty(response!.ResultRemark) ? response!.ResultRemark : `Unknown error ${response!.ResultCode}`
        );
      }

      let navigateToChanged = false;
      if (
        response!.ResultCode === ResultCode.ODDS_CHANGED ||
        response!.ResultCode === ResultCode.SCORE_CHANGED ||
        response!.ResultCode === ResultCode.INSUFFICIENT_FUNDS
      ) {
        navigateToChanged = true;
      }

      await dispatch(fetchBalance());

      return { response, navigateToChanged: navigateToChanged ?? false };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        // throw new Error(e.response?.data.Message?.replace(/^.*?message=/g, ''));
        return rejectWithValue(e.response?.data.Message?.replace(/^.*?message=/g, ''));
      }
    }
  }
);

export const doSubmitBet = createAsyncThunk('cart/doSubmitBet', async (_, { getState, dispatch }) => {
  const { cart } = getState() as RootState;
  let data: any;
  if (cart.type === BettingCartType.Single) {
    data = await dispatch(placeSingleBet()).unwrap();
  } else {
    data = await dispatch(placeParlayBet()).unwrap();
  }
  await dispatch(doGetMiniBetList());
  return data;
});

export const validateSingleBet = createAsyncThunk('cart/validateSingleBet', async (_, { getState, dispatch }) => {
  const { cart } = getState() as RootState;
  for (const bet of cart.cart.singleBet) {
    if (typeof bet.BetArgs.BetAmt === 'string') {
      dispatch(updateSingleBetAmount({ BetArgs: { ...bet.BetArgs, BetAmt: Number(bet.BetArgs.BetAmt) } } as any));
      const isValid =
        checkSingleBetExceededLimitSelector(getState() as RootState, {
          id: bet.BetArgs.id!,
          sportType: bet.Match.SportType,
        }) === ExceedState.NotExceeded;
      dispatch(touchSingleBetError({ id: bet.BetArgs.id!, isError: !isValid }));
    }
  }

  return selectIsAnySingleBetError(getState() as RootState);
});

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    toggleCart: (
      state,
      action: PayloadAction<boolean | BettingCartType | { isOpen?: boolean; type: BettingCartType }>
    ) => {
      if (typeof action.payload === 'boolean') {
        state.isOpen = action.payload;
      } else if (typeof action.payload === 'object') {
        state.isOpen = action.payload.isOpen ?? state.isOpen;
        state.type = action.payload.type;
      } else {
        let defaultIsOpen = true;
        // if current cart (single or parlay) is empty, set isOpen to true
        if (state.type === BettingCartType.Single && state.cart.singleBet.length > 0) {
          defaultIsOpen = state.isOpen;
        }
        if (state.type === BettingCartType.Parlay && state.cart.parlayBet) {
          defaultIsOpen = state.isOpen;
        }

        state.isOpen = defaultIsOpen;
        state.type = action.payload;
      }
    },
    updateSingleBetAmount: (state, action: PayloadAction<SingleBetPayload>) => {
      const { id } = action.payload.BetArgs;
      const bet = state.cart.singleBet.find((item) => item.BetArgs.id === id);
      if (bet) {
        bet.BetArgs = action.payload.BetArgs;
        delete bet._insufficientFunds;
      }

      state.singleBetSelectedId = id;
    },
    updateParlayBetParameters: (state, action: PayloadAction<Partial<ParlayBetRequest>>) => {
      if (action.payload.CombineFlag !== undefined) {
        state.cart.parlayBet!.CombineFlag = action.payload.CombineFlag;
      }
    },
    updateParlayBetAmount: (state, action: PayloadAction<string>) => {
      if (!state.cart.parlayBet) {
        state.cart.parlayBet = {
          BetArgs: [],
          CombineFlag: 0,
          BetterOdds: false,
          AOSCores: 0,
        };
      }
      state.cart.parlayBetAmt = Number(action.payload);
      state.cart.parlayBet!.BetArgs.forEach((item) => {
        item.BetAmt = Number(action.payload);
      });

      delete state.cart.parlayBetResultDetail;
      delete state.cart.resultCode;
    },
    removeMatchFromSingleBetByBetId: (state, action: PayloadAction<string>) => {
      state.cart.singleBet = state.cart.singleBet.filter((item) => item.BetArgs.id !== action.payload);
      delete state.cart.isSingleBetExceeded[action.payload];
    },
    removeMatchFromParlayBetByMatchId: (state, action: PayloadAction<number>) => {
      state.cart.parlayBet!.BetArgs = state.cart.parlayBet!.BetArgs.filter(
        (item) => item.MatchID !== Number(action.payload)
      );
    },
    removeMatchFromParlayBetByBetId: (state, action: PayloadAction<string>) => {
      state.cart.parlayBet!.BetArgs = state.cart.parlayBet!.BetArgs.filter((item) => item.id !== action.payload);
    },
    selectSingleBet: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload === undefined) {
        delete state.singleBetSelectedId;
      } else state.singleBetSelectedId = action.payload;
    },
    clearCart: (state) => {
      state.cart = {
        currency: 'USD',
        totalBet: '0',
        totalBetAmount: '0',
        singleBet: [],
        parlayInfo: [],
        parlayBetAmt: 0,
        isSingleBetExceeded: {},
      };
      state.bets = [];
      state.singleBetSelectedId = undefined;
    },
    setStep: (state, action: PayloadAction<CartSteps>) => {
      if (state.currentStep === 'ERROR' && action.payload === 'BETTING') {
        // delete all current single bet and parlay bet
        state.cart.singleBet = [];
        state.cart.parlayBet = undefined;
      }
      state.currentStep = action.payload;
      delete state.betError;
    },
    touchSingleBetError: (state, action: PayloadAction<{ id: string; isError: boolean }>) => {
      const { id, isError } = action.payload;
      state.cart.isSingleBetExceeded[id] = isError;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addMatchToSingleBet.fulfilled, (state, action) => {
        const isMatchExist = state.cart.singleBet.find((item) => item.BetArgs.id === action.payload.BetArgs.id);
        if (!isMatchExist) {
          state.cart.singleBet.push(action.payload);
          state.singleBetSelectedId = action.payload.BetArgs.id;
        }
      })
      .addCase(addMatchToParlayBet.fulfilled, (state, action) => {
        if (!state.cart.parlayBet) {
          state.cart.parlayBet = {
            BetArgs: [],
            CombineFlag: 0,
            BetterOdds: false,
            AOSCores: 0,
            Match: action.payload.Match,
          };
        }

        const isMatchExist = state.cart.parlayBet.BetArgs.find(
          (item) => item.MatchID === action.payload.BetArgs.MatchID
        );

        if (!isMatchExist) {
          state.cart.parlayBet!.BetArgs.push({ ...action.payload.BetArgs, id: action.payload.BetArgs.id });
        } else {
          const index = state.cart.parlayBet!.BetArgs.findIndex(
            (item) => item.MatchID === action.payload.BetArgs.MatchID
          );

          state.cart.parlayBet!.BetArgs[index] = { ...action.payload.BetArgs, id: action.payload.BetArgs.id };
        }
      })
      .addCase(addMultipleMatchesToParlayBet.fulfilled, (state, action) => {
        if (!state.cart.parlayBet) {
          state.cart.parlayBet = {
            BetArgs: [],
            CombineFlag: 0,
            BetterOdds: false,
            AOSCores: 0,
            Match: action.payload.Match,
          };
        }

        const _parlayBetArgs = state.cart.parlayBet.BetArgs;
        for (const payload of action.payload.BetArgs) {
          const isMatchExist = _parlayBetArgs.find((item) => item.MatchID === payload.MatchID);
          if (!isMatchExist) {
            _parlayBetArgs.push({ ...payload, id: payload.id });
          } else {
            const index = _parlayBetArgs.findIndex((item) => item.MatchID === payload.MatchID);
            _parlayBetArgs[index] = { ...payload, id: payload.id };
          }
        }
        state.cart.parlayBet.BetArgs = _parlayBetArgs;
        state.cart.parlayBetAmt = action.payload.BetArgs[0].BetAmt;
      })
      .addCase(placeSingleBet.pending, (state) => {
        // iterate through bets, remove all _changed props
        for (const bet of state.cart.singleBet) {
          delete bet._changed;
          delete bet._paused;
        }
      })
      .addCase(placeSingleBet.fulfilled, (state, action) => {
        const { response: bets } = action.payload;

        for (const { id, bet } of bets) {
          const betIndex = state.cart.singleBet.findIndex((singleBet) => singleBet.BetArgs.id === id);

          // in case of success, remove the bet from cart
          if (betIndex !== -1 && [ResultCode.SUCCESS_NORMAL, ResultCode.SUCCESS_DANGER].includes(bet.ResultCode)) {
            state.cart.singleBet.splice(betIndex, 1);

            // if (bet) (state.bets as SingleBetDTO[]).push(bet);
          }

          // in case of change, update the bet in cart
          if (betIndex !== -1 && [ResultCode.ODDS_CHANGED, ResultCode.SCORE_CHANGED].includes(bet.ResultCode)) {
            const oldBet = state.cart.singleBet.find((item) => item.BetArgs.id === id)!;
            // iterate through props in bet.BetArgs, update the value in bet.BetArgs if it is different from the response
            for (const prop of [
              'AOSCores',
              'OddsValue',
              'OddsChanged',
              'HomeScore',
              'AwayScore',
              'SpecialValue',
              'IsHomeGive',
            ] as const) {
              if (bet.ResultDetail[prop]) {
                const oldValue = oldBet.BetArgs[prop];

                //@ts-ignore
                oldBet.BetArgs[prop] = bet.ResultDetail[prop];

                if (action.payload.navigateToChanged) {
                  const changedId = `${state.type}_${ServiceUtils.getBetRequestId({
                    BetArgs: oldBet.BetArgs,
                  })}`;
                  oldBet.BetArgs.id = changedId;
                  state.singleBetSelectedId = changedId;
                  const match = oldBet.Match;
                  const isFH = oldBet.BetArgs.IsFH;
                  const specialValue = oldBet.BetArgs.SpecialValue;

                  if (oldBet.BetArgs.BetType === BetType.HDP) {
                    const foundHDPIndex = match.HDP_List.findIndex((item) => {
                      if (isFH) {
                        return Number(item.HDP_SpecialValue_FH) === oldBet.BetArgs.SpecialValue;
                      }
                      return Number(item.HDP_SpecialValue_FT) === oldBet.BetArgs.SpecialValue;
                    });
                    if (foundHDPIndex !== -1) {
                      oldBet.BetArgs._indexed = foundHDPIndex;
                    }

                    if (Array.isArray(oldBet.BetArgs.BetPositionLabel)) {
                      const isHomeGive = oldBet.BetArgs.IsHomeGive;
                      let specialValueLabel: BetPositionLabelType = '';

                      if (oldBet.BetArgs.BetPosition === 'HOME') {
                        specialValueLabel = [
                          ['TeamLeagueName', TeamLeagueType.TEAM_NAME, match.HomeTeamID, match.SpecialID],
                          ` ${Number(specialValue) > 0 ? (isHomeGive ? '-' : '+') : ''}${specialValue}`,
                        ];
                      }

                      if (oldBet.BetArgs.BetPosition === 'AWAY') {
                        specialValueLabel = [
                          ['TeamLeagueName', TeamLeagueType.TEAM_NAME, match.AwayTeamID, match.SpecialID],
                          ` ${Number(specialValue) > 0 ? (isHomeGive ? '+' : '-') : ''}${specialValue}`,
                        ];
                      }

                      if (specialValueLabel) {
                        oldBet.BetArgs.BetPositionLabel = specialValueLabel;
                      }
                    }
                  }
                }

                if (!oldBet._changed) {
                  oldBet._changed = {};
                }

                //@ts-ignore
                oldBet._changed[prop as ChangeableKeys] = [oldValue, bet.ResultDetail[prop]];
              }
            }

            // remove duplicated bet in singleBet
            const dict: Record<string, boolean> = {};
            let currentIndex = 0;
            for (let i = 0; i < state.cart.singleBet.length; i++) {
              const id = state.cart.singleBet[i].BetArgs.id!;
              if (!dict[id]) {
                dict[id] = true;
                state.cart.singleBet[currentIndex] = state.cart.singleBet[i];
                currentIndex++;
              }
            }
            state.cart.singleBet.length = currentIndex;
          }

          // in case of hide or paused, set _paused to true
          if (betIndex !== -1 && [ResultCode.MATCH_HIDE, ResultCode.MATCH_PAUSED].includes(bet.ResultCode)) {
            const oldBet = state.cart.singleBet.find((item) => item.BetArgs.id === id)!;
            oldBet._paused = true;
          }

          if (betIndex !== -1 && [ResultCode.INSUFFICIENT_FUNDS].includes(bet.ResultCode)) {
            const oldBet = state.cart.singleBet.find((item) => item.BetArgs.id === id)!;
            oldBet._insufficientFunds = true;
          }
        }
      })
      .addCase(placeParlayBet.pending, (state) => {
        // in case parlayBetResultDetail is present,
        // assign its OddsValue to the parlayBet.BetArgs, then delete it

        if ((state.cart.parlayBet?.CombineFlag ?? 0) > 0)
          state.cart.parlayBet!.BetArgs.forEach((item) => {
            item.BetAmt = state.cart.parlayBetAmt * state.cart.parlayBet!.CombineFlag;
          });

        if (state.cart.parlayBetResultDetail) {
          for (const index in state.cart.parlayBetResultDetail.OddsChangedDetail) {
            const item = state.cart.parlayBetResultDetail.OddsChangedDetail[index];
            const bet = state.cart.parlayBet!.BetArgs[index];
            if (item && bet) {
              bet.OddsValue = item.OddsValue;
              bet.SpecialValue = item.SpecialValue;
              bet.HomeScore = item.HomeScore;
              bet.AwayScore = item.AwayScore;
              bet.IsHomeGive = item.IsHomeGive;
              bet.AOSCores = item.AOSCores;
            }
          }
          delete state.cart.parlayBetResultDetail;
        }
      })
      .addCase(placeParlayBet.fulfilled, (state, action) => {
        const navigateToChanged = action.payload?.navigateToChanged;
        const response = action.payload?.response;

        // if (action.payload) state.bets = action.payload.response;
        if (navigateToChanged === true) {
          state.cart.parlayBetResultDetail = response!.ResultDetail;
          state.cart.resultCode = response!.ResultCode;
        } else {
          state.cart.parlayBet = undefined;
        }
      })
      .addCase(doSubmitBet.rejected, (state, action) => {
        state.currentStep = 'ERROR';
        state.betError = action.error.message;
      })
      .addCase(getMatchesByMatchIds.fulfilled, (state, action) => {
        const matches = action.payload;
        if (action.meta.arg.singleCartPolling) {
          state.cart.singleBet.forEach((bet) => {
            const match = matches.find((match) => match.MatchID === bet.Match.MatchID);
            if (match && !isEqual(bet.Match, match)) {
              Object.assign(bet.Match, match);
              Object.assign(bet.BetArgs, ServiceUtils.calculateOddValueFromMatchAndBetArgs(match, bet.BetArgs));
              bet.BetArgs.OddsValue = calculateOddByOddsType(
                bet.BetArgs.OddsValue,
                bet.BetArgs.OddsType,
                bet.BetArgs.BetType
              );
              bet.BetArgs.HomeScore = match.HomeTeamScore;
              bet.BetArgs.AwayScore = match.AwayTeamScore;
              bet.BetArgs.id = `${state.type}_${ServiceUtils.getBetRequestId({
                BetArgs: bet.BetArgs,
              })}`;
            }
          });
        }

        if (action.meta.arg.parlayCartPolling) {
          state.cart.parlayBet?.BetArgs.forEach((betArg) => {
            const match = matches.find((match) => match.MatchID === betArg.MatchID);
            if (match) {
              Object.assign(betArg, ServiceUtils.calculateOddValueFromMatchAndBetArgs(match, betArg, true));
              betArg.OddsValue = calculateOddByOddsType(betArg.OddsValue, betArg.OddsType, betArg.BetType);
              betArg.HomeScore = match.HomeTeamScore;
              betArg.AwayScore = match.AwayTeamScore;
              betArg.id = `${state.type}_${ServiceUtils.getBetRequestId({
                BetArgs: betArg,
              })}`;
            }
          });
        }

        state.pollingTs = Date.now();
      })
      .addCase(getMatchByMatchGroupId.fulfilled, (state, action) => {
        const match = action.payload;

        state.cart.singleBet.forEach((bet) => {
          if (match.MatchGroupID === bet.Match.MatchGroupID && !isEqual(bet.Match, match)) {
            Object.assign(bet.Match, match);
            Object.assign(bet.BetArgs, ServiceUtils.calculateOddValueFromMatchAndBetArgs(match, bet.BetArgs));
            bet.BetArgs.OddsValue = calculateOddByOddsType(
              bet.BetArgs.OddsValue,
              bet.BetArgs.OddsType,
              bet.BetArgs.BetType
            );
            bet.BetArgs.HomeScore = match.HomeTeamScore;
            bet.BetArgs.AwayScore = match.AwayTeamScore;
            bet.BetArgs.id = `${state.type}_${ServiceUtils.getBetRequestId({
              BetArgs: bet.BetArgs,
            })}`;
          }
        });

        state.pollingTs = Date.now();
      });
  },
});

export const {
  toggleCart,
  selectSingleBet,
  updateParlayBetAmount,
  updateParlayBetParameters,
  updateSingleBetAmount,
  removeMatchFromSingleBetByBetId,
  removeMatchFromParlayBetByMatchId,
  removeMatchFromParlayBetByBetId,
  clearCart,
  setStep,
  touchSingleBetError,
} = cartSlice.actions;

export const selectCartStep = (state: RootState) => state.cart.currentStep;
export const selectIsAnySingleBetError = createSelector(
  (state: RootState) => state.cart.cart.isSingleBetExceeded,
  (isSingleBetExceeded) => Object.values(isSingleBetExceeded).some((isExceeded) => isExceeded === true)
);
export enum ExceedState {
  NotExceeded,
  LessThanMin,
  MoreThanMax,
}
export const checkSingleBetExceededLimitSelector = createSelector(
  getSingleBetPayloadSelector,
  getBetLimitBySportTypeSelector,
  (singleBet, betLimit) => {
    if (!singleBet || !betLimit) return ExceedState.NotExceeded;
    if ((singleBet.BetArgs.BetAmt as unknown as string) === '') return ExceedState.NotExceeded;
    if (singleBet.BetArgs.BetAmt < betLimit.MinBetLimit) return ExceedState.LessThanMin;
    if (singleBet.BetArgs.BetAmt > betLimit.MaxBetLimit) return ExceedState.MoreThanMax;
    return ExceedState.NotExceeded;
  }
);
export default cartSlice.reducer;
